import type { Session, SessionMessage } from '@orgus/protocol';
import { HEADLESS_MESSAGE_MAX_CHARS, SESSION_MESSAGE_STATE_LABEL, agentKindLabel, agentKindOf } from '@orgus/protocol';
import { CircleCheck, CircleDashed, CircleX, Clock, PlugZap, Send } from '@orgus/ui/icons';
import { useState } from 'react';
import type { KeyboardEvent } from 'react';
import { Button, ConfirmDialog, ErrorState, RelativeTime, Textarea, cn, formatClock, formatInt, toast } from '@orgus/ui';
import { useMachine } from '../../lib/store.ts';
import { closeInput, sendMessage, useMe, useMessages } from './api.ts';
import type { MessagesData } from './api.ts';
import { composerState } from './model.ts';
import { replyBoxId, useReplyFill } from './replyDraft.ts';

const STATE_LOOK = {
  sending: { icon: CircleDashed, cls: 'text-fg-subtle' },
  sent: { icon: CircleCheck, cls: 'text-fg-muted' },
  delivered: { icon: CircleCheck, cls: 'text-passed' },
  failed: { icon: CircleX, cls: 'text-failed' },
} as const;

function MessageItem({ m }: { m: SessionMessage }) {
  const look = STATE_LOOK[m.state];
  const Icon = look.icon;
  return (
    <li className="flex flex-col gap-1 rounded-card rounded-tr-control bg-accent-soft px-3 py-2">
      <p className="whitespace-pre-wrap break-words text-base text-fg">{m.text}</p>
      <p className="flex items-center gap-2 text-caption text-fg-subtle">
        <span>{m.by}</span>
        <span className="num">{formatClock(m.at)}</span>
        <span className={cn('ml-auto inline-flex items-center gap-1 font-medium', look.cls)}>
          <Icon className="size-3.5" aria-hidden />
          {SESSION_MESSAGE_STATE_LABEL[m.state]}
        </span>
      </p>
      {m.state === 'failed' && m.error && <p className="text-sm text-failed">{m.error}</p>}
    </li>
  );
}

/**
 * Send messages to a session Orgus launched headless: its agent reads each one from its
 * stream-json input as the next prompt. History is kept by the hub and audited. A session that
 * can no longer take one and was never sent one shows nothing.
 */
export function Messages({ session, data: shared }: { session: Session; /** The tab's own fetch, shared with MessageEntry. */ data?: MessagesData }) {
  const me = useMe();
  const machine = useMachine(session.machineId);
  const live = session.status !== 'ended';
  const own = useMessages(session, !shared);
  const data = shared ?? own;
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<unknown>(null);
  const [closeOpen, setCloseOpen] = useState(false);
  // A quick reply from the waiting card lands here, focused and not sent (ADR 0056).
  useReplyFill(session.id, setText);
  const c = composerState(data.input, { loading: data.loading, online: !!machine?.online, machineName: machine?.name ?? 'The machine', role: me.role });
  const tooLong = text.length > HEADLESS_MESSAGE_MAX_CHARS;
  const agent = agentKindLabel(agentKindOf(session));

  const send = async () => {
    const t = text.trim();
    if (!t || !c.canSend || tooLong) return;
    setSending(true);
    setSendError(null);
    try {
      await sendMessage(session, t);
      setText('');
    } catch (e) {
      // Inline, under the message it was about: errors are never a toast (content.md).
      setSendError(e);
    } finally {
      setSending(false);
      data.reload();
    }
  };

  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      void send();
    }
  };

  // An ended session takes no messages: the box shows only the history it has, never a load error.
  if (!live && !data.messages.length) return null;

  return (
    <section aria-label="Messages to this session" className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-medium text-fg-muted">Messages</h3>
        {data.input?.accepting ? (
          <span className="inline-flex items-center gap-1 text-caption font-medium text-passed">
            <PlugZap className="size-3.5" aria-hidden />
            Takes messages
          </span>
        ) : (
          data.input && (
            <span className="inline-flex items-center gap-1 text-caption font-medium text-fg-subtle">
              <PlugZap className="size-3.5" aria-hidden />
              Not taking messages
            </span>
          )
        )}
        {data.input?.accepting && data.input.closesAt && (
          <span className="inline-flex items-center gap-1 text-caption text-fg-subtle">
            <Clock className="size-3" aria-hidden />
            Input closes <RelativeTime ts={data.input.closesAt} /> if idle
          </span>
        )}
      </div>
      {data.error && <ErrorState variant="inline" error={data.error} onRetry={data.reload} />}
      {data.messages.length > 0 ? (
        <ol className="flex flex-col gap-2">
          {data.messages.map((m) => (
            <MessageItem key={m.id} m={m} />
          ))}
        </ol>
      ) : (
        <p className="text-caption text-fg-subtle">Each message goes to {agent} as its next prompt.</p>
      )}
      {live && (
        <div className="flex flex-col gap-2">
          <Textarea
            id={replyBoxId(session.id)}
            rows={3}
            aria-label="Message to send"
            placeholder={c.canSend ? `Message ${agent}` : ''}
            value={text}
            disabled={!c.canSend || sending}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKey}
            hint={tooLong ? undefined : c.hint}
            error={tooLong ? `A message is at most ${formatInt(HEADLESS_MESSAGE_MAX_CHARS)} characters.` : undefined}
          />
          {sendError != null && <ErrorState variant="inline" error={sendError} />}
          <div className="flex items-center gap-2">
            {data.input?.accepting && (
              <Button variant="ghost" size="sm" onClick={() => setCloseOpen(true)}>
                Close input
              </Button>
            )}
            <Button
              variant="primary"
              size="sm"
              className="ml-auto"
              icon={<Send />}
              shortcut={['mod', 'enter']}
              loading={sending}
              disabled={!c.canSend || !text.trim() || tooLong}
              onClick={() => void send()}
            >
              Send
            </Button>
          </div>
        </div>
      )}
      <ConfirmDialog
        open={closeOpen}
        onOpenChange={setCloseOpen}
        tone="primary"
        title="Close this session's input?"
        description={`The session ends once ${agent} finishes.`}
        confirmLabel="Close input"
        onConfirm={async () => {
          await closeInput(session);
          data.reload();
          toast.success('Input closed', 'The session finishes its current work and exits.');
        }}
      />
    </section>
  );
}
